import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { showToastr } from '../../../components/ToastrMessage/Toast.helper';
import useTranslation from '../../../translations/use-translation';
import './CSCOwnerProfile.styles.scss';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar', 'Chandigarh', 'Delhi', 'Jammu & Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const getStoredProfile = () => {
  try { return JSON.parse(localStorage.getItem('cscOwnerProfile')) || {}; }
  catch { return {}; }
};

const CSCOwnerProfile = () => {
  const translate = useTranslation();
  const stored = getStoredProfile();

  const [form, setForm] = useState({
    centerName:  stored.centerName  || '',
    mobile:      stored.mobile      || '',
    address:     stored.address     || '',
    villageTown: stored.villageTown || '',
    pincode:     stored.pincode     || '',
    city:        stored.city        || '',
    state:       stored.state       || '',
    mapLocation: stored.mapLocation || '',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.centerName.trim())       e.centerName  = 'CSC center name is required';
    if (!form.mobile.trim())           e.mobile      = 'Mobile number is required';
    else if (!/^\d{10}$/.test(form.mobile)) e.mobile = 'Enter a valid 10-digit number';
    if (!form.address.trim())          e.address     = 'Address is required';
    if (!form.villageTown.trim())      e.villageTown = 'Village / Town is required';
    if (!form.pincode.trim())          e.pincode     = 'Pincode is required';
    else if (!/^\d{6}$/.test(form.pincode)) e.pincode = 'Enter a valid 6-digit pincode';
    if (!form.city.trim())             e.city        = 'City is required';
    if (!form.state)                   e.state       = 'State is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const updated = { ...stored, ...form };
      localStorage.setItem('cscOwnerProfile', JSON.stringify(updated));

      // Keep cscCenters in sync — upsert this owner's center entry so their
      // services appear in the service-centers map for users.
      const centerId = stored.id || `owner-${updated.email || Date.now()}`;
      const centerEntry = {
        id:         centerId,
        centerName: updated.centerName,
        ownerName:  updated.ownerName || '',
        address:    updated.address,
        city:       updated.city,
        state:      updated.state,
        pincode:    updated.pincode,
        mobile:     updated.mobile,
        email:      updated.email || '',
        serviceCharges: {},
      };
      try {
        const centers = JSON.parse(localStorage.getItem('cscCenters') || '[]');
        const idx = centers.findIndex((c) => c.id === centerId);
        if (idx >= 0) centers[idx] = { ...centers[idx], ...centerEntry };
        else centers.push(centerEntry);
        localStorage.setItem('cscCenters', JSON.stringify(centers));
      } catch { /* storage full */ }

      showToastr('success', 'Profile updated successfully.');
    } catch (_) {
      showToastr('error', 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cop-profile">
      <div className="cop-profile__header">
        <h2 className="cop-profile__title">{translate('CSC_PROFILE_HEADING')}</h2>
        <p className="cop-profile__subtitle">{translate('CSC_PROFILE_SUBTITLE')}</p>
      </div>

      {/* Read-only identity fields */}
      <div className="cop-profile__readonly-row">
        <TextField
          size="small" variant="outlined" fullWidth
          label={translate('OWNER_NAME')}
          value={stored.ownerName || ''}
          InputProps={{ readOnly: true }}
          className="cop-profile__readonly-field"
        />
        <TextField
          size="small" variant="outlined" fullWidth
          label={translate('EMAIL_LABEL')}
          value={stored.email || ''}
          InputProps={{ readOnly: true }}
          className="cop-profile__readonly-field"
        />
      </div>

      <div className="cop-profile__divider" />

      {/* Editable fields */}
      <div className="cop-profile__form">
        <div className="cop-profile__row">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('CSC_CENTER_NAME')}
            name="centerName"
            value={form.centerName}
            onChange={handleChange}
            error={!!errors.centerName}
            helperText={errors.centerName}
            placeholder="e.g. Rajesh Kumar Seva Kendra"
          />
        </div>

        <div className="cop-profile__row">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('MOBILE_NO')}
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            error={!!errors.mobile}
            helperText={errors.mobile}
            inputProps={{ maxLength: 10 }}
          />
        </div>

        <div className="cop-profile__row">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('CSC_ADDRESS')}
            name="address"
            value={form.address}
            onChange={handleChange}
            error={!!errors.address}
            helperText={errors.address}
          />
        </div>

        <div className="cop-profile__row cop-profile__row--2col">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('VILLAGE_TOWN')}
            name="villageTown"
            value={form.villageTown}
            onChange={handleChange}
            error={!!errors.villageTown}
            helperText={errors.villageTown}
          />
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('PINCODE')}
            name="pincode"
            value={form.pincode}
            onChange={handleChange}
            error={!!errors.pincode}
            helperText={errors.pincode}
            inputProps={{ maxLength: 6 }}
          />
        </div>

        <div className="cop-profile__row cop-profile__row--2col">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('CITY')}
            name="city"
            value={form.city}
            onChange={handleChange}
            error={!!errors.city}
            helperText={errors.city}
          />
          <TextField
            select
            size="small" variant="outlined" fullWidth
            label={translate('STATE')}
            name="state"
            value={form.state}
            onChange={handleChange}
            error={!!errors.state}
            helperText={errors.state}
          >
            {INDIAN_STATES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </div>

        <div className="cop-profile__row">
          <TextField
            size="small" variant="outlined" fullWidth
            label={translate('MAP_LOCATION')}
            name="mapLocation"
            value={form.mapLocation}
            onChange={handleChange}
            placeholder="Paste Google Maps link or enter coordinates"
          />
        </div>

        <Button
          variant="contained"
          disableElevation
          className="cop-profile__save-btn"
          onClick={handleSave}
          disabled={saving}
          endIcon={saving && <CircularProgress color="inherit" size={16} />}
        >
          {saving ? 'Saving...' : translate('CSC_SAVE_CHANGES')}
        </Button>
      </div>
    </div>
  );
};

export default CSCOwnerProfile;
